// src/components/AppNav.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { fetchSiteContent } from '../services/firebase';
import NotificationBell from './NotificationBell';

// ── SVG Icons for bottom tab bar ──
const IconHome = ({ active }) => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={active ? 'var(--mom-primary)' : 'var(--mom-warm-gray)'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
);

const IconPrograms = ({ active }) => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={active ? 'var(--mom-primary)' : 'var(--mom-warm-gray)'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
        <line x1="8" y1="21" x2="16" y2="21" />
        <line x1="12" y1="17" x2="12" y2="21" />
    </svg>
);

const IconPlay = ({ active }) => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={active ? 'var(--mom-primary)' : 'var(--mom-warm-gray)'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <polygon points="10 8 16 12 10 16 10 8" fill={active ? 'var(--mom-primary)' : 'none'} />
    </svg>
);

const IconLive = ({ active }) => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={active ? 'var(--mom-primary)' : 'var(--mom-warm-gray)'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="4" fill={active ? 'var(--mom-primary)' : 'none'} />
        <path d="M16.24 7.76a6 6 0 0 1 0 8.49" />
        <path d="M7.76 16.24a6 6 0 0 1 0-8.49" />
        <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
        <path d="M4.93 19.07a10 10 0 0 1 0-14.14" />
    </svg>
);

const IconAdmin = ({ active }) => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={active ? 'var(--mom-primary)' : 'var(--mom-warm-gray)'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
);

const IconAccount = ({ active }) => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={active ? 'var(--mom-primary)' : 'var(--mom-warm-gray)'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
    </svg>
);

function AppNav({ demoMode }) {
    const [dashLabel, setDashLabel] = useState(null); // null = not loaded yet
    const [workoutsEnabled, setWorkoutsEnabled] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    const clerkData = useUser();
    let isAdmin = false;
    let isFriend = false;
    let isSignedIn = false;
    let userId = null;

    if (!demoMode) {
        const user = clerkData?.user;
        isAdmin = user?.publicMetadata?.role === 'admin';
        isFriend = user?.publicMetadata?.role === 'friend';
        isSignedIn = !!user;
        userId = user?.id || null;
    } else {
        isAdmin = true;
        isSignedIn = true;
    }

    useEffect(() => {
        fetchSiteContent().then((c) => {
            setDashLabel((c && c.dashboardNavLabel) || "Let's Move");
            setWorkoutsEnabled(!!(c && c.workoutsPageEnabled));
        });
    }, []);

    const handleNav = (path) => { navigate(path); };
    const currentPath = location.pathname;

    const labelsReady = dashLabel !== null;

    // Nav tabs adapt based on auth state and feature flag
    const tabs = isSignedIn ? [
        { label: dashLabel || "Let's Move", path: '/dashboard', icon: IconHome },
        { label: 'Programs', path: '/programs', icon: IconPrograms },
        ...(workoutsEnabled
            ? [{ label: 'Workouts', path: '/workouts', icon: IconLive }]
            : [
                { label: 'Free Videos', path: '/free-workouts', icon: IconPlay },
                ...((isAdmin || isFriend) ? [{ label: 'Workouts', path: '/workouts', icon: IconLive }] : []),
            ]
        ),
        // Admins get Admin AND Account (their own profile, photo, tagline,
        // password). Members keep the original 4 tabs, unchanged. Five tabs
        // fit the mobile bottom bar (minWidth 60 × 5 < 320px).
        ...(isAdmin ? [{ label: 'Admin', path: '/admin', icon: IconAdmin }] : []),
        { label: 'Account', path: '/account', icon: IconAccount },
    ] : [
        { label: 'Home', path: '/landing', icon: IconHome },
        { label: 'Programs', path: '/programs', icon: IconPrograms },
    ];

    const isActive = (path) => {
        if (path === '/dashboard') return currentPath === '/dashboard';
        if (path === '/account') return currentPath === '/account';
        return currentPath.startsWith(path);
    };

    const previewAsUser = typeof window !== 'undefined' && localStorage.getItem('mom-preview-as-user') === 'true';

    return (
        <>
            {/* Preview mode banner */}
            {isAdmin && previewAsUser && (
                <div style={{ background: 'var(--mom-charcoal)', color: 'var(--mom-white)', textAlign: 'center', padding: '6px 16px', fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, zIndex: 101, position: 'relative' }}>
                    <span>👁 Previewing as user</span>
                    <button onClick={() => { localStorage.removeItem('mom-preview-as-user'); window.location.reload(); }}
                        style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: 'var(--mom-white)', padding: '2px 10px', borderRadius: 6, cursor: 'pointer', fontFamily: 'var(--mom-font-sans)', fontSize: 11, fontWeight: 600 }}>
                        Exit Preview
                    </button>
                </div>
            )}
            {/* ════════════════════════════════════════════════════
                DESKTOP TOP BAR (hidden on mobile via CSS)
               ════════════════════════════════════════════════════ */}
            <nav className="mom-glass-header mom-desktop-nav" style={{
                padding: '0 24px',
                position: 'sticky',
                top: 0,
                zIndex: 100,
            }}>
                <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 70 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, cursor: 'pointer' }} onClick={() => handleNav('/landing')}>
                        <span style={{ fontFamily: 'var(--mom-font-serif)', fontWeight: 700, fontSize: 22, fontStyle: 'italic', color: 'var(--mom-charcoal)' }}>moments of movement</span>
                        <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--mom-warm-gray)' }}>with Danielle</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, opacity: labelsReady ? 1 : 0, transition: 'opacity 0.2s' }}>
                        {tabs.map((item) => (
                            <button key={item.path} className="mom-btn mom-btn-ghost" onClick={() => handleNav(item.path)}
                                style={{
                                    color: isActive(item.path) ? 'var(--mom-link)' : 'var(--mom-charcoal)',
                                    fontWeight: isActive(item.path) ? 700 : 500,
                                    borderBottom: isActive(item.path) ? '2px solid var(--mom-link)' : '2px solid transparent',
                                    borderRadius: 0,
                                    paddingBottom: 4,
                                    fontSize: 14,
                                    letterSpacing: '0.01em',
                                    transition: 'all 0.2s',
                                }}>
                                {item.label}
                            </button>
                        ))}
                        {isSignedIn && userId && (
                            <span style={{ marginLeft: 6, display: 'inline-flex' }}>
                                <NotificationBell userId={userId} />
                            </span>
                        )}
                    </div>
                </div>
            </nav>

            {/* ════════════════════════════════════════════════════
                MOBILE TOP BAR (hidden on desktop) — just logo
               ════════════════════════════════════════════════════ */}
            <nav className="mom-glass-header mom-mobile-topbar" style={{
                padding: '0 20px',
                position: 'sticky',
                top: 0,
                zIndex: 100,
            }}>
                <div onClick={() => handleNav('/landing')} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 52, cursor: 'pointer' }}>
                    <span style={{ fontFamily: 'var(--mom-font-serif)', fontWeight: 700, fontSize: 18, fontStyle: 'italic', color: 'var(--mom-charcoal)', lineHeight: 1 }}>moments of movement</span>
                    <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 8, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--mom-warm-gray)', marginTop: 3 }}>with Danielle</span>
                </div>
                {isSignedIn && userId && (
                    <div style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)' }}>
                        <NotificationBell userId={userId} />
                    </div>
                )}
            </nav>

            {/* ════════════════════════════════════════════════════
                MOBILE BOTTOM TAB BAR (hidden on desktop)
               ════════════════════════════════════════════════════ */}
            <nav className="mom-glass-footer mom-mobile-bottomnav" style={{
                position: 'fixed',
                bottom: 0,
                left: 0,
                right: 0,
                zIndex: 100,
                paddingBottom: 'env(safe-area-inset-bottom, 0px)',
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-around',
                    height: 64,
                    maxWidth: 500,
                    margin: '0 auto',
                }}>
                    {tabs.map((tab) => {
                        const active = isActive(tab.path);
                        const TabIcon = tab.icon;
                        return (
                            <button
                                key={tab.path}
                                onClick={() => handleNav(tab.path)}
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 3,
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: '6px 12px',
                                    position: 'relative',
                                    WebkitTapHighlightColor: 'transparent',
                                    touchAction: 'manipulation',
                                    transition: 'all 0.2s',
                                    minWidth: 60,
                                }}
                            >
                                {/* Glowing active indicator bar */}
                                {active && (
                                    <div style={{
                                        position: 'absolute',
                                        top: -1,
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        width: 24,
                                        height: 2,
                                        borderRadius: 2,
                                        background: 'var(--mom-primary)',
                                        boxShadow: '0 0 8px color-mix(in srgb, var(--mom-primary) 50%, transparent)',
                                    }} />
                                )}
                                <TabIcon active={active} />
                                <span style={{
                                    fontFamily: 'var(--mom-font-sans)',
                                    fontSize: 10,
                                    fontWeight: active ? 700 : 500,
                                    color: active ? 'var(--mom-primary)' : 'var(--mom-warm-gray)',
                                    letterSpacing: '0.01em',
                                    lineHeight: 1,
                                    whiteSpace: 'nowrap',
                                    transition: 'color 0.2s, font-weight 0.2s, opacity 0.2s',
                                    opacity: labelsReady ? 1 : 0,
                                }}>
                                    {tab.label}
                                </span>
                            </button>
                        );
                    })}
                </div>
            </nav>
        </>
    );
}

export default AppNav;
